import type { createBridgeLifecycleTracker } from '#~/analytics/domains/bridge-lifecycle.js';
import type { AccountBootstrapPickerMode } from '#~/routes/root/account-bootstrap-decision.js';
import type { AccountStudioLinkAttempt } from './account-studio-operations.js';
import { setupErrorType } from './setup-error-type.js';

type BridgeLinkTracker = Pick<
  ReturnType<typeof createBridgeLifecycleTracker>,
  'trackAutoLinkAttempt' | 'trackAutoLinkFailure' | 'trackAutoLinkSuccess'
>;

export type PickerBridgeLinkAction = 'add' | 'create' | 'link' | 'replace';

export interface PickerBridgeLinkFunnel {
  /** Pass to the link operation; it fires right before the cloud link mutation. */
  readonly onLinkAttempt: (attempt: AccountStudioLinkAttempt) => void;
  /** Call once the operation has verified the link. */
  readonly succeeded: () => void;
  /** Call from the operation's failure path. */
  readonly failed: (cause: unknown) => void;
  /** The Desktop Bridge the link request named; undefined before the attempt. */
  readonly attemptedAgentId: () => AccountStudioLinkAttempt['agentId'] | undefined;
}

/**
 * Reports one picker link operation on the canonical bridge_link_* funnel.
 *
 * When the account already has Studios, boot skips auto-link and the Bridge
 * is linked here instead, so without these rows every such link is missing
 * from the funnel auto-link reports. The rows use the same shared sender as
 * auto-link and add `surface`, `picker_mode` and `action` so they can be told
 * apart from auto-link rows.
 *
 * The funnel opens only at the attempt, because `request_id` is the sidecar's
 * link request and does not exist earlier. A failure before that (auth, the
 * link request itself, the Studio create) is reported by the studio_* events
 * and never here. After the attempt exactly one terminal fires, success or
 * failure, even if the caller reaches both.
 * Per rules: settle-every-terminal-branch, reference-rules-in-comments
 * (ai.knowledge/global).
 */
export const startPickerBridgeLinkFunnel = (
  tracker: BridgeLinkTracker,
  source: {
    readonly action: PickerBridgeLinkAction;
    readonly pickerMode: AccountBootstrapPickerMode;
  }
): PickerBridgeLinkFunnel => {
  let attempt: AccountStudioLinkAttempt | undefined;
  let settled = false;

  const properties = (current: AccountStudioLinkAttempt) => ({
    request_id: current.requestId,
    room_id: current.roomId,
    agent_id: current.agentId,
    surface: 'electron_account_bootstrap',
    picker_mode: source.pickerMode,
    action: source.action
  });

  return {
    onLinkAttempt: (next) => {
      // One operation makes one link request; a repeat would double-count it.
      if (attempt !== undefined) return;
      attempt = next;
      tracker.trackAutoLinkAttempt(properties(next));
    },
    succeeded: () => {
      if (attempt === undefined || settled) return;
      settled = true;
      tracker.trackAutoLinkSuccess(properties(attempt));
    },
    failed: (cause) => {
      if (attempt === undefined || settled) return;
      settled = true;
      tracker.trackAutoLinkFailure({ ...properties(attempt), reason: setupErrorType(cause) });
    },
    attemptedAgentId: () => attempt?.agentId
  };
};
